<template>
  <div
    class="dark border-b border-blue-900 from-blue-950 to-black text-white shadow-section-dark bg-gradient-to-b pt-8 pb-20 -mb-16"
  >
    <s-page-block size="md" padding="horizontal">
      <div class="flex items-center justify-between flex-wrap-reverse gap-x-1 gap-y-2">
        <h1 class="grow text-4xl flex items-center gap-2.5">
          <s-logo icon class="h-12" />
          {{ t('page.heading') }}
        </h1>
        <!-- Link back to dashboard -->
        <div class="flex-1 flex justify-end">
          <s-link
            :href="route('dashboard')"
            class="whitespace-nowrap underline underline-offset-2 decoration-transparent hover:decoration-blue-200/50 active:decoration-blue-300 decoration-2 font-medium text-blue-300 hover:text-blue-200 active:text-blue-400 tracking-slight transition-all ease-out duration-100 flex items-center gap-1.5"
          >
            <s-icon name="arrow-left" class="opacity-70" />
            {{ t('backToDashboard') }}
          </s-link>
        </div>
      </div>
    </s-page-block>
  </div>
  <s-page-block size="md">
    <ul class="flex flex-col gap-5">
      <li v-for="post in posts" :key="post.slug">
        <s-card class="cursor-pointer group" @click="(event) => handleCardClick(event, post.slug)">
          <template #header>
            <div class="flex items-center justify-between gap-2">
              <h2 class="text-2xl md:text-3xl leading-tight font-normal">
                <s-link
                  :href="route('blog.show', {slug: post.slug})"
                  class="underline underline-offset-2 decoration-transparent group-hover:decoration-blue-200/50 group-active:decoration-blue-300 decoration-2 group-hover:text-blue-500 group-active:text-blue-700 transition"
                >
                  {{ post.title }}
                </s-link>
              </h2>
              <s-badge v-if="!post.publishedAt" icon="pencil" class="mb-1">
                {{ t('draft') }}
              </s-badge>
            </div>
            <p
              v-if="post.publishedAt"
              class="flex items-center gap-1 text-lg text-gray-600 font-medium mt-1"
            >
              <s-icon name="calendar" class="opacity-70" />
              {{ monthDayYear(post.publishedAt) }}
            </p>
          </template>

          <template #content>
            <div class="px-5 md:px-7 lg:px-8 pt-4 md:pt-5 pb-4 md:pb-6 lg:pb-7">
              <p>{{ post.description }}</p>
            </div>
          </template>
        </s-card>
      </li>
    </ul>
  </s-page-block>
</template>
<script setup lang="ts">
import SPageBlock from '../../design-system/SPageBlock.vue';
import {useI18n} from 'vue-i18n';
import SLogo from '../../design-system/SLogo.vue';
import {monthDayYear} from '../../format/dates';
import PostData = App.DTOs.Blog.PostData;
import BlogLayout from '../../layouts/BlogLayout.vue';
import SCard from '../../design-system/SCard.vue';
import SIcon from '../../design-system/SIcon.vue';
import SLink from '../../components/SLink.vue';
import SBadge from '../../design-system/SBadge.vue';
import {route} from 'ziggy-js';
import {router} from '@inertiajs/vue3';

defineProps<{
  posts: PostData[];
}>();

defineOptions({
  layout: BlogLayout,
});

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const handleCardClick = (event: MouseEvent, slug: string) => {
  // Don't trigger if clicking directly on the link or its children
  if (event.target instanceof HTMLElement && event.target.closest('a')) {
    return;
  }

  router.visit(route('blog.show', {slug}));
};
</script>
<i18n>
{
  "en": {
    "page.heading": "Stemble Product Blog",
    "links.read": "Read more",
    "backToDashboard": "Back to Dashboard"
  },
  "fr": {
    "page.heading": "Blog de produit de Stemble",
    "links.read": "Lire la suite",
    "backToDashboard": "Retour au tableau de bord"
  }
}
</i18n>
